import QuestionContainer from "@/components/QuestionContainer";
import SlideFooter from "@/components/SlideFooter";
import { Logic, QuestionAnswer } from "@/types/questions";
import { useRef, useState } from "preact/hooks";

interface BubbleSelectProps {
  onBack: (selectedAnswer: QuestionAnswer | null) => void;
  onNext: (selectedAnswer: QuestionAnswer) => void;
  questionText: string;
  answers: QuestionAnswer[];
  logic?: Logic;
  defaultSelectedAnswers: QuestionAnswer | null;
}

const BubbleSelect = ({
  onBack,
  onNext,
  questionText,
  answers,
  defaultSelectedAnswers,
}: BubbleSelectProps) => {
  const [selectedAnswer, setSelectedAnswer] = useState<QuestionAnswer | null>(
    defaultSelectedAnswers
  );

  const handleNext = () => {
    if (selectedAnswer) {
      onNext(selectedAnswer);
    }
  };

  const handleBack = () => {
    onBack(selectedAnswer);
  };

  const radioRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleKeyDown = (e: KeyboardEvent, index: number) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      const nextIndex = (index + 1) % answers.length;
      radioRefs.current[nextIndex]?.focus();
      setSelectedAnswer(answers[nextIndex]);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      const prevIndex = (index - 1 + answers.length) % answers.length;
      radioRefs.current[prevIndex]?.focus();
      setSelectedAnswer(answers[prevIndex]);
    } else if (e.key === " ") {
      setSelectedAnswer(answers[index]);
    }
  };

  const styles = [
    {
      ringClassNames: "lc-w-[120px] lc-h-[120px] lg:lc-w-[140px] lg:lc-h-[140px]",
      containerClassNames: "lc-self-start lg:lc-mt-24 lg:-lc-mr-6",
    },
    {
      ringClassNames: "lc-w-[130px] lc-h-[130px] lg:lc-w-[160px] lg:lc-h-[160px]",
      containerClassNames: "lc-self-end -lc-mt-20 -lc-mr-4 lg:-lc-ml-6 lg:lc-mr-0 lg:lc-mt-0",
    },
    {
      ringClassNames: "lc-w-[140px] lc-h-[140px] lg:lc-w-[180px] lg:lc-h-[180px]",
      containerClassNames: "lc-self-start -lc-mt-16 -lc-ml-6 lg:-lc-ml-12 lg:lc-mt-16",
    },
    {
      ringClassNames: "lc-w-[150px] lc-h-[150px] lg:lc-w-[200px] lg:lc-h-[200px]",
      containerClassNames: "lc-self-end -lc-mt-20 -lc-mr-4 lg:-lc-ml-8 lg:lc-mt-0",
    },
    {
      ringClassNames: "lc-w-[160px] lc-h-[160px] lg:lc-w-[250px] lg:lc-h-[250px]",
      containerClassNames: "lc-self-start -lc-mt-12 -lc-ml-6 lg:lc-mt-0 lg:-lc-ml-8",
    },
  ];

  return (
    <>
      <QuestionContainer legendText={questionText} role="radiogroup">
        <div
          className={`lc-flex lc-flex-col lg:lc-flex-row lc-w-full lc-self-center lc-justify-center lc-max-w-[260px] lg:lc-max-w-6xl lg:lc-h-96 lg:-lc-mb-8 ${
            answers.length === 4 ? "lg:-lc-mt-20" : "lg:-lc-mt-8"
          }`}
        >
          {answers.map((answer, index) => (
            <div
              className={`lc-flex lc-items-center lc-justify-center lc-cursor-pointer ${styles[index].containerClassNames} lc-p-[10px]`}
            >
              <div
                key={answer.id}
                id={answer.id}
                role="radio"
                tabIndex={
                  index === 0 && !selectedAnswer
                    ? 0
                    : selectedAnswer?.id === answer.id
                    ? 0
                    : -1
                }
                aria-checked={selectedAnswer?.id === answer.id}
                ref={(el) => (radioRefs.current[index] = el)}
                class={`${
                  styles[index].ringClassNames
                } lc-rounded-full !lc-border-solid !lc-border-[10px] lc-cursor-pointer lg:!lc-border-[15px] lc-flex lc-items-center lc-justify-center lc-transition lc-duration-300 lc-ease-in-out
            ${
              selectedAnswer?.id === answer.id
                ? "lc-border-white"
                : "!lc-border-cfa-blue-700 hover:!lc-border-cfa-blue-800 focus:!lc-border-cfa-blue-800 active:!lc-border-cfa-blue-800"
            }`}
                onClick={() => setSelectedAnswer(answer)}
                onKeyDown={(e: KeyboardEvent) => handleKeyDown(e, index)}
              >
                <label
                  htmlFor={answer.id}
                  className="lc-text-white lc-text-base lc-font-azo-sans lc-p-2 lc-text-center lc-cursor-pointer"
                >
                  {answer.text}
                </label>
              </div>
            </div>
          ))}
        </div>
      </QuestionContainer>
      <SlideFooter
        onNext={handleNext}
        onBack={handleBack}
        disableContinue={!selectedAnswer}
      />
    </>
  );
};

export default BubbleSelect;
